/**
 * Generic event emitter for handling application-wide events
 */
export class EventEmitter<T extends Record<string, (...args: any[]) => void>> {
  private events: { [K in keyof T]?: T[K][] } = {};

  /**
   * Subscribe to an event
   */
  on<K extends keyof T>(event: K, handler: T[K]): void {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event]!.push(handler);
  }

  /**
   * Unsubscribe from an event
   */
  off<K extends keyof T>(event: K, handler: T[K]): void {
    if (!this.events[event]) return;

    const index = this.events[event]!.indexOf(handler);
    if (index > -1) {
      this.events[event]!.splice(index, 1);
    }
  }

  /**
   * Emit an event to all subscribers
   */
  emit<K extends keyof T>(event: K, ...args: Parameters<T[K]>): void {
    if (!this.events[event]) return;

    this.events[event]!.forEach(handler => {
      try {
        handler(...args);
      } catch (error) {
        console.error(`Error in event handler for ${String(event)}:`, error);
      }
    });
  }

  /**
   * Subscribe to an event once (auto-unsubscribe after first emission)
   */
  once<K extends keyof T>(event: K, handler: T[K]): void {
    const onceHandler = ((...args: Parameters<T[K]>) => {
      handler(...args);
      this.off(event, onceHandler as T[K]);
    }) as T[K];

    this.on(event, onceHandler);
  }

  /**
   * Remove all subscribers for a specific event
   */
  removeAllListeners<K extends keyof T>(event?: K): void {
    if (event) {
      this.events[event] = [];
    } else {
      this.events = {};
    }
  }

  /**
   * Get the number of subscribers for an event
   */
  listenerCount<K extends keyof T>(event: K): number {
    return this.events[event]?.length || 0;
  }
}